import {
  Button,
  Calendar,
  RangeCalendar,
  CalendarCell,
  CalendarGrid,
  DatePicker as AdobeDatePicker,
  DateRangePicker as AdobeDateRangePicker,
  Dialog,
  Group,
  Heading,
  Popover,
} from 'react-aria-components';
import { UNSAFE_PortalProvider } from '@react-aria/overlays';
import {
  ComponentProps,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DatePickerStyles, DateRangePickerStyles } from './date-picker.styles';
import {
  Calendar as CalendarIcon,
  LeftChevron,
  RightChevron,
  SugarIcon,
} from '../../../icons';
import { join, noop, omit, constant } from 'lodash';
import { ZonedDateTime } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useRenderDate, useDatepickerProps } from './date-picker.hook';
import { defaultDate } from './date-picker.utils';
import { DatePickerChildrenProps } from './date-picker.interfaces';
import classnames from 'classnames';
import { messages } from '../../../i18n';
import { useIsDashletMode, useIsResponsive } from '../../../auth';
import { enableShadowDOM } from '@react-stately/flags';
import { ClickOutsideListener } from '../../ClickOutsideListener';

/**
 * NOTE that these components are derived from Adobe's react-aria components
 */

// derived from https://react-spectrum.adobe.com/react-aria/DatePicker.html
export const DatePicker = forwardRef<
  HTMLDivElement,
  ComponentProps<typeof AdobeDatePicker> & {
    defaultValue?: ZonedDateTime;
    showTime?: boolean;
  }
>(
  (
    {
      onChange: _onChange,
      isDisabled,
      defaultValue = defaultDate(),
      granularity = 'day',
      hideTimeZone = true,
      isOpen,
      showTime = false,
      ...remainingProps
    },
    forwardingRef,
  ) => {
    const [selectedDate, setSelectedDate] =
      useState<ZonedDateTime>(defaultValue);
    const onChangeSpy = useCallback(
      (date: ZonedDateTime) => {
        setSelectedDate(date);
        _onChange(date);
      },
      [_onChange],
    );

    const { onChange, wrapperProps, datePickerChildrenProps } =
      useDatepickerProps({
        isOpen,
        isDisabled,
        onChange: onChangeSpy,
      });

    const formatDate = useRenderDate(showTime);
    const formattedDate = formatDate(selectedDate);

    // prevent console logs about 'key'
    const spreadProps: Omit<
      ComponentProps<typeof AdobeDatePicker>,
      'onChange' | 'isDisabled' | 'granularity' | 'hideTimeZone' | 'isOpen'
    > = omit(remainingProps, ['key']);

    return (
      <DatePickerStyles {...wrapperProps}>
        <AdobeDatePicker
          ref={forwardingRef}
          onChange={onChange}
          isDisabled={isDisabled}
          defaultValue={defaultValue}
          granularity={granularity}
          hideTimeZone={hideTimeZone}
          {...spreadProps}
        >
          <DatePickerChildren
            {...datePickerChildrenProps}
            formattedDate={formattedDate}
            CalendarComponent={Calendar}
          />
        </AdobeDatePicker>
      </DatePickerStyles>
    );
  },
);

// derived from https://react-spectrum.adobe.com/react-aria/DateRangePicker.html
export const DateRangePicker = forwardRef<
  any,
  ComponentProps<typeof AdobeDateRangePicker> & {
    defaultValue?: RangeValue<ZonedDateTime>;
    showTime?: boolean;
  }
>(
  (
    {
      onChange: _onChange,
      isDisabled,
      defaultValue = {
        start: defaultDate(),
        end: defaultDate(),
      },
      granularity = 'day',
      hideTimeZone = true,
      isOpen,
      showTime = false,
      ...remainingProps
    },
    forwardingRef,
  ) => {
    const [selectedRange, setSelectedDate] =
      useState<RangeValue<ZonedDateTime>>(defaultValue);
    const onChangeSpy = useCallback(
      (range: RangeValue<ZonedDateTime>) => {
        setSelectedDate(range);
        _onChange(range);
      },
      [_onChange],
    );

    const { onChange, wrapperProps, datePickerChildrenProps } =
      useDatepickerProps({
        isOpen,
        isDisabled,
        onChange: onChangeSpy,
      });

    // prevent console logs about 'key'
    const spreadProps: Omit<
      ComponentProps<typeof AdobeDateRangePicker>,
      'onChange' | 'isDisabled' | 'granularity' | 'hideTimeZone' | 'isOpen'
    > = omit(remainingProps, ['key']);

    const formatDate = useRenderDate(showTime);

    const formattedDate = useMemo(() => {
      const { start: startDateValue, end: endDateValue } = selectedRange;
      const startDate = formatDate(startDateValue);
      const endDate = formatDate(endDateValue);
      return join([startDate, endDate], ' - ');
    }, [formatDate, selectedRange]);

    return (
      <DateRangePickerStyles {...wrapperProps}>
        <AdobeDateRangePicker
          ref={forwardingRef}
          onChange={onChange}
          isDisabled={isDisabled}
          defaultValue={defaultValue}
          granularity={granularity}
          hideTimeZone={hideTimeZone}
          {...spreadProps}
        >
          <DatePickerChildren
            {...datePickerChildrenProps}
            formattedDate={formattedDate}
            CalendarComponent={RangeCalendar}
          />
        </AdobeDateRangePicker>
      </DateRangePickerStyles>
    );
  },
);

const DateLabel = ({ children, onClick = noop, showIcon = false }) => (
  <div
    data-testid={'date-label'}
    className={classnames('react-aria-DateInput', showIcon && 'with-icon')}
    role={'presentation'}
    onClick={onClick}
  >
    {children}
  </div>
);

// DatePicker and DateRangePicker share the same child render
export const DatePickerChildren = memo(
  ({
    toggleInternalOpen,
    formattedDate,
    isExternallyControlled,
    isPopoverOpen,
    portalContainer,
    shouldCloseOnInteractOutside,
    CalendarComponent = Calendar,
  }: DatePickerChildrenProps) => {
    const isDashletMode = useIsDashletMode();
    const isResponsive = useIsResponsive();

    // See this issue & fix (https://github.com/adobe/react-spectrum/pull/6046)
    // associated with clicking/focus in our dashlet shadow environment.
    // this feature flag will be enabled by default in a later adobe version
    useEffect(() => {
      isDashletMode && enableShadowDOM();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderPopover = (
      <UNSAFE_PortalProvider
        getContainer={() => portalContainer}
        key={'calendar-popover-container'}
      >
        <Popover
          key={'calendar-popover'}
          isOpen={isPopoverOpen}
          shouldCloseOnInteractOutside={
            isExternallyControlled
              ? constant(false)
              : shouldCloseOnInteractOutside
          }
        >
          <Dialog id={'date-picker-dialog'}>
            <CalendarComponent id={'date-picker-calendar'}>
              {({ state }) => {
                const subtractYear = () => {
                  state.setFocusedDate(
                    state.focusedDate.subtract({ years: 1 }),
                  );
                };

                const addYear = () => {
                  state.setFocusedDate(state.focusedDate.add({ years: 1 }));
                };
                return (
                  <>
                    <header>
                      <Button
                        id={'date-picker-year-button-left'}
                        slot='previous'
                        onPress={subtractYear}
                      >
                        <SugarIcon icon='arrow-left-double' />
                      </Button>
                      <Button id={'date-picker-button-left'} slot='previous'>
                        <LeftChevron />
                      </Button>
                      <Heading />
                      <Button id={'date-picker-button-right'} slot='next'>
                        <RightChevron />
                      </Button>
                      <Button
                        id={'date-picker-year-button-right'}
                        slot='next'
                        onPress={addYear}
                      >
                        <SugarIcon icon='arrow-right-double' />
                      </Button>
                    </header>
                    <CalendarGrid>
                      {date => <CalendarCell date={date} />}
                    </CalendarGrid>
                  </>
                );
              }}
            </CalendarComponent>
          </Dialog>
        </Popover>
      </UNSAFE_PortalProvider>
    );

    return [
      !isResponsive && (
        <Group
          id={'date-picker-group'}
          onClick={toggleInternalOpen}
          key={'date-field'}
        >
          <DateLabel showIcon={!isExternallyControlled}>
            {formattedDate}
          </DateLabel>
          {!isExternallyControlled && (
            <span>
              <Button>
                <CalendarIcon aria-label={messages.editDatasetPanel.setDate} />
              </Button>
            </span>
          )}
        </Group>
      ),
      // truthiness of portalContainer here prevents a flash effect
      portalContainer && isExternallyControlled && (
        <ClickOutsideListener
          key={'calendar-popover-wrapper'}
          isChildrenVisible={isPopoverOpen}
          onClickAway={() => shouldCloseOnInteractOutside(portalContainer)}
        >
          {renderPopover}
        </ClickOutsideListener>
      ),
      portalContainer && !isExternallyControlled && renderPopover,
    ].filter(Boolean);
  },
);
